import {
  Outlet,
  Link,
  createRootRoute,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import "../styles.css";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { initAnalytics, trackPageView } from "../lib/analytics";
import { SiteHeader, SiteFooter, CallBar } from "../components/site-chrome";

/** After the LCP image preload so fonts do not occupy the first connections. */
const FONT_PRELOADS = [
  {
    rel: "preload" as const,
    as: "font" as const,
    type: "font/woff2",
    href: "/fonts/karla-latin.woff2",
    crossOrigin: "anonymous" as const,
    fetchPriority: "low" as const,
  },
  {
    rel: "preload" as const,
    as: "font" as const,
    type: "font/woff2",
    href: "/fonts/cormorant-garamond-latin-300.woff2",
    crossOrigin: "anonymous" as const,
    fetchPriority: "low" as const,
  },
  {
    rel: "preload" as const,
    as: "font" as const,
    type: "font/woff2",
    href: "/fonts/cormorant-garamond-latin-300-italic.woff2",
    crossOrigin: "anonymous" as const,
    fetchPriority: "low" as const,
  },
];

type LcpImagePreload = {
  rel: "preload";
  as: "image";
  href?: string;
  type?: string;
  imageSrcSet?: string;
  imageSizes?: string;
  fetchPriority?: "high" | "low" | "auto";
};

function isLcpImagePreload(link: unknown): link is LcpImagePreload {
  if (!link || typeof link !== "object") return false;
  const attrs = link as Record<string, unknown>;
  return attrs["rel"] === "preload" && attrs["as"] === "image";
}

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="eyebrow">404</p>
        <h1 className="display mt-4 text-5xl text-foreground">This wall is empty</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center justify-center border border-primary/50 px-6 py-3 text-[11px] uppercase tracking-[0.26em] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            Back home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="display text-4xl text-foreground">This page didn't load</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Something went wrong on our end. Try again, or call Shirley at (919) 552-6802.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="border border-primary/50 px-6 py-3 text-[11px] uppercase tracking-[0.26em] text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
          >
            Try again
          </button>
          <a
            href="/"
            className="border border-border px-6 py-3 text-[11px] uppercase tracking-[0.26em] text-foreground transition-colors hover:border-primary"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { property: "og:site_name", content: "Magnolia House Custom Picture Framing" },
      {
        name: "description",
        content:
          "Certified Professional Framer Shirley Hunsberger has designed custom, archival picture framing in Fuquay-Varina since 1989. By appointment — call (919) 552-6802.",
      },
      { name: "author", content: "Magnolia House Custom Picture Framing" },
      { name: "theme-color", content: "#1c2748" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "google-site-verification",
        content: "ZuurXXld1mH4piHWfEoVkcZwFVNQiFByBhSzAta-tU0",
      },
    ],
    links: [
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/favicon-32.png", type: "image/png", sizes: "32x32" },
      { rel: "icon", href: "/favicon-16.png", type: "image/png", sizes: "16x16" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
      { rel: "manifest", href: "/site.webmanifest" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const lcp = useRouterState({
    select: (s) => {
      for (let i = s.matches.length - 1; i >= 0; i--) {
        const links = s.matches[i]?.links;
        if (!Array.isArray(links)) continue;
        const found = links.find(isLcpImagePreload);
        if (found) return found;
      }
      return undefined;
    },
  });

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        {lcp ? (
          <link
            rel="preload"
            as="image"
            href={lcp.href}
            type={lcp.type}
            imageSrcSet={lcp.imageSrcSet}
            imageSizes={lcp.imageSizes}
            fetchPriority="high"
          />
        ) : null}
        <HeadContent />
        {FONT_PRELOADS.map((font) => (
          <link key={font.href} {...font} />
        ))}
        <noscript>
          <style>{`.reveal{opacity:1!important;transform:none!important;filter:none!important}`}</style>
        </noscript>
      </head>
      <body>
        <a href="#main-content" className="skip-link">
          Skip to content
        </a>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  return (
    <>
      <SiteHeader />
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <main id="main-content" tabIndex={-1} key={pathname}>
        <Outlet />
      </main>
      <SiteFooter />
      <CallBar />
    </>
  );
}
